import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'

// ── Constants ──────────────────────────────────────────────────────────────────

const COVER_GRADIENTS = [
  { label: 'Twilight', value: 'linear-gradient(135deg, #1E0A3C 0%, #3B0764 35%, #831843 100%)' },
  { label: 'Ocean',    value: 'linear-gradient(135deg, #0F4C75 0%, #1E0A3C 50%, #3B0764 100%)' },
  { label: 'Forest',   value: 'linear-gradient(135deg, #064E3B 0%, #0F4C75 50%, #1E0A3C 100%)' },
  { label: 'Cosmic',   value: 'linear-gradient(135deg, #3B0764 0%, #831843 50%, #1D4ED8 100%)' },
  { label: 'Ember',    value: 'linear-gradient(135deg, #831843 0%, #FF6B35 50%, #3B0764 100%)' },
  { label: 'Stellar',  value: 'linear-gradient(135deg, #1D4ED8 0%, #7C3AED 50%, #EC4899 100%)' },
]

const COMMON_FANDOMS = [
  'General', 'Original Fiction', 'Harry Potter', 'My Hero Academia', 'Naruto',
  'Attack on Titan', 'Jujutsu Kaisen', 'Haikyuu', 'BTS RPF', 'Marvel',
  'Star Wars', 'Vampire Diaries', 'One Piece', 'Demon Slayer', 'Other',
]

function uid() { return Math.random().toString(36).slice(2) }

function makeAnswer(type) {
  return { id: uid(), text: '', isCorrect: false, outcomeTempId: null }
}

function makeQuestion(type) {
  return {
    id: uid(),
    text: '',
    answers: [makeAnswer(type), makeAnswer(type), makeAnswer(type), makeAnswer(type)],
  }
}

function makeOutcome() {
  return { tempId: uid(), title: '', description: '' }
}

const DEFAULT_QUIZ = {
  type:        'knowledge',
  title:       '',
  description: '',
  fandom:      'General',
  tags:        [],
  coverIdx:    0,
  disableComments: false,
  randomizeAnswers: false,
  outcomes:    [],
  questions:   [],
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function buildPayload(quiz, published) {
  return {
    type:        quiz.type,
    title:       quiz.title,
    description: quiz.description,
    fandom:      quiz.fandom,
    tags:        quiz.tags,
    coverIdx:    quiz.coverIdx,
    disable_comments: quiz.disableComments,
    randomize_answers: quiz.randomizeAnswers,
    published,
    outcomes: quiz.type === 'personality' ? quiz.outcomes.map(o => ({
      tempId:      o.tempId ?? o.id,
      title:       o.title,
      description: o.description,
    })) : [],
    questions: quiz.questions.map(q => ({
      text: q.text,
      answers: q.answers.map(a => ({
        text:          a.text,
        isCorrect:     quiz.type === 'knowledge' ? a.isCorrect : false,
        outcomeTempId: quiz.type === 'personality' ? (a.outcomeTempId ?? null) : null,
        outcomeId:     a.outcomeId ?? null,
      })),
    })),
  }
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function TagInput({ tags, onChange }) {
  const [input, setInput] = useState('')
  function add() {
    const v = input.trim().toLowerCase()
    if (!v || tags.includes(v) || tags.length >= 10) return
    onChange([...tags, v])
    setInput('')
  }
  return (
    <div className="qcr-tag-input">
      <div className="qcr-tags">
        {tags.map(t => (
          <span key={t} className="qcr-tag">
            #{t}
            <button type="button" onClick={() => onChange(tags.filter(x => x !== t))}>×</button>
          </span>
        ))}
      </div>
      <div className="qcr-tag-row">
        <input
          className="qcr-input qcr-input--sm"
          placeholder="Add tag…"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
          maxLength={30}
        />
        <button type="button" className="qcr-tag-add" onClick={add} disabled={!input.trim()}>+</button>
      </div>
    </div>
  )
}

function QuestionEditor({ question, quizType, outcomes, onChange, onDelete, questionNum, totalQuestions }) {
  function setAnswerText(aId, text) {
    onChange({ ...question, answers: question.answers.map(a => a.id === aId ? { ...a, text } : a) })
  }
  function setCorrect(aId) {
    onChange({ ...question, answers: question.answers.map(a => a.id === aId ? { ...a, isCorrect: !a.isCorrect } : a) })
  }
  function setOutcome(aId, outcomeTempId) {
    onChange({ ...question, answers: question.answers.map(a => a.id === aId ? { ...a, outcomeTempId } : a) })
  }
  function addAnswer() {
    if (question.answers.length >= 6) return
    onChange({ ...question, answers: [...question.answers, makeAnswer(quizType)] })
  }
  function removeAnswer(aId) {
    if (question.answers.length <= 2) return
    onChange({ ...question, answers: question.answers.filter(a => a.id !== aId) })
  }

  return (
    <div className="qcr-question-editor">
      <div className="qcr-question-editor__header">
        <span className="qcr-question-num">Question {questionNum} of {totalQuestions}</span>
        {totalQuestions > 1 && (
          <button type="button" className="qcr-delete-q" onClick={onDelete} title="Delete question">
            Delete
          </button>
        )}
      </div>

      <textarea
        className="qcr-question-text"
        placeholder="Ask your question…"
        value={question.text}
        onChange={e => onChange({ ...question, text: e.target.value })}
        rows={3}
        maxLength={500}
      />

      <div className="qcr-answers">
        <div className="qcr-answers__label">
          {quizType === 'knowledge' ? 'Answers — click ○ to mark correct (multiple allowed)' : 'Answers — assign each to an outcome'}
        </div>

        {question.answers.map((a, i) => (
          <div key={a.id} className="qcr-answer-row">
            {quizType === 'knowledge' ? (
              <button
                type="button"
                className={`qcr-correct-btn${a.isCorrect ? ' qcr-correct-btn--on' : ''}`}
                onClick={() => setCorrect(a.id)}
                title="Mark as correct"
              >
                {a.isCorrect ? '✓' : '○'}
              </button>
            ) : (
              <select
                className="qcr-outcome-select"
                value={a.outcomeTempId ?? ''}
                onChange={e => setOutcome(a.id, e.target.value || null)}
              >
                <option value="">— outcome —</option>
                {outcomes.map(o => (
                  <option key={o.tempId ?? o.id} value={o.tempId ?? o.id}>
                    {o.title || `Outcome ${outcomes.indexOf(o) + 1}`}
                  </option>
                ))}
              </select>
            )}

            <input
              type="text"
              className="qcr-input qcr-answer-input"
              placeholder={`Answer ${String.fromCharCode(65 + i)}`}
              value={a.text}
              onChange={e => setAnswerText(a.id, e.target.value)}
              maxLength={200}
            />

            {question.answers.length > 2 && (
              <button type="button" className="qcr-remove-answer" onClick={() => removeAnswer(a.id)} title="Remove">×</button>
            )}
          </div>
        ))}

        {question.answers.length < 6 && (
          <button type="button" className="qcr-add-answer" onClick={addAnswer}>+ Add answer</button>
        )}
      </div>
    </div>
  )
}

// ── Page ────────────────────────────────────────────────────────────────────────

export default function QuizCreatorPage() {
  const { quizId }    = useParams()
  const navigate      = useNavigate()
  const { getToken }  = useAuth()

  const [quiz, setQuiz]         = useState({ ...DEFAULT_QUIZ, questions: [makeQuestion('knowledge')] })
  const [activeQIdx, setActiveQ] = useState(0)
  const [loading, setLoading]   = useState(!!quizId)
  const [saving, setSaving]     = useState(false)
  const [saved, setSaved]       = useState(false)
  const [error, setError]       = useState(null)
  const wasPublished = useRef(false)

  const [coverFile, setCoverFile]           = useState(null)
  const [coverPreview, setCoverPreview]     = useState(null)
  const [existingCoverUrl, setExistingCoverUrl] = useState(null)
  const coverInputRef = useRef(null)

  // Auto-save draft to localStorage (backup for tab close / refresh)
  const DRAFT_KEY = quizId ? `verse_quiz_draft_${quizId}` : 'verse_quiz_draft_new'
  const saveTimer = useRef(null)
  const autoSaveTriggered = useRef(false)
  const savingInProgress = useRef(false)
  const createdIdRef = useRef(null)

  // Save to localStorage on changes
  useEffect(() => {
    clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ quiz, savedAt: Date.now() }))
      } catch (err) { console.error('[QuizCreatorPage] Failed to save draft to localStorage:', err) }
    }, 1500)
    return () => clearTimeout(saveTimer.current)
  }, [quiz])

  // Auto-save draft to server when user leaves the page
  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState === 'hidden' && !autoSaveTriggered.current) {
        autoSaveTriggered.current = true
        save(false, true).finally(() => { autoSaveTriggered.current = false })
      }
    }
    function onBeforeUnload(e) {
      if (!saved) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [saved])

  async function handleBack() {
    const hasContent = quiz.title.trim() || quiz.questions.some(q => q.text.trim())
    if (hasContent && (quizId || createdIdRef.current)) {
      await save(false, true)
    }
    navigate('/quizzes')
  }

  // Load existing quiz for editing
  useEffect(() => {
    if (!quizId) return
    async function load() {
      try {
        const token = await getToken()
        const { data } = await axios.get(`/api/quizzes/${quizId}`, { headers: { Authorization: `Bearer ${token}` } })
        // Map API format back to creator format
        const outcomes = (data.outcomes ?? []).map(o => ({ ...o, tempId: o.id }))
        const questions = (data.questions ?? []).map(q => ({
          id: q.id,
          text: q.text,
          answers: (q.answers ?? []).map(a => ({
            id:            a.id,
            text:          a.text,
            isCorrect:     a.isCorrect,
            outcomeTempId: a.outcomeId,  // use outcomeId as tempId for existing
            outcomeId:     a.outcomeId,
          })),
        }))
        setQuiz({
          type:        data.type,
          title:       data.title ?? '',
          description: data.description ?? '',
          fandom:      data.fandom ?? 'General',
          tags:        data.tags ?? [],
          coverIdx:    data.coverIdx ?? 0,
          disableComments: data.disableComments ?? false,
          randomizeAnswers: data.randomizeAnswers ?? false,
          outcomes,
          questions: questions.length ? questions : [makeQuestion(data.type)],
        })
        if (data.coverUrl) setExistingCoverUrl(data.coverUrl)
        if (data.published) wasPublished.current = true
      } catch (err) { console.error('[QuizCreatorPage] Failed to load quiz data:', err) }
      finally { setLoading(false) }
    }
    load()
  }, [quizId, getToken])

  function updateQuiz(patch) { setQuiz(q => ({ ...q, ...patch })); setSaved(false) }
  function updateQuestion(idx, q) { setQuiz(prev => { const qs = [...prev.questions]; qs[idx] = q; return { ...prev, questions: qs } }); setSaved(false) }
  function addQuestion() {
    const newQ = makeQuestion(quiz.type)
    setQuiz(q => ({ ...q, questions: [...q.questions, newQ] }))
    setActiveQ(quiz.questions.length)
  }
  function deleteQuestion(idx) {
    if (quiz.questions.length <= 1) return
    const qs = quiz.questions.filter((_, i) => i !== idx)
    setQuiz(q => ({ ...q, questions: qs }))
    setActiveQ(Math.min(activeQIdx, qs.length - 1))
  }

  async function save(publish, silent = false) {
    if (savingInProgress.current) return
    if (!silent) setError(null)

    // Don't save empty drafts (no title and no question text)
    const hasContent = quiz.title.trim() || quiz.questions.some(q => q.text.trim())
    if (!publish && !hasContent) return

    // Never silently auto-save over a published quiz — it could wipe content
    if (silent && wasPublished.current) return

    // Validate only when publishing
    if (publish) {
      if (!quiz.title.trim()) return !silent && setError('Please add a title.')
      if (!quiz.questions.every(q => q.text.trim())) return !silent && setError('All questions need text.')
      if (quiz.type === 'knowledge') {
        if (!quiz.questions.every(q => q.answers.some(a => a.isCorrect))) return !silent && setError('Each question needs a correct answer.')
      }
      if (quiz.type === 'personality') {
        if (!quiz.outcomes.length) return !silent && setError('Personality quizzes need at least one outcome.')
      }
    }

    savingInProgress.current = true
    if (!silent) setSaving(true)
    try {
      const token = await getToken()
      const payload = buildPayload(quiz, publish)
      const headers = { Authorization: `Bearer ${token}` }

      const existingId = quizId || createdIdRef.current
      let id = existingId
      if (existingId) {
        await axios.put(`/api/quizzes/${existingId}`, payload, { headers })
      } else {
        const { data } = await axios.post('/api/quizzes', payload, { headers })
        id = data.id
        createdIdRef.current = id
      }

      if (coverFile && !silent) {
        const form = new FormData()
        form.append('file', coverFile)
        await axios.post(`/api/quizzes/${id}/cover`, form, { headers })
        setCoverFile(null)
      }

      localStorage.removeItem(DRAFT_KEY)

      if (publish) {
        wasPublished.current = true
        navigate(`/quiz/${id}`)
      } else if (!silent) {
        if (!quizId) navigate(`/quiz/edit/${id}`, { replace: true })
        setSaved(true)
        setTimeout(() => setSaved(false), 3000)
      }
    } catch (err) {
      if (!silent) setError(err.response?.data?.error ?? 'Save failed.')
    } finally {
      savingInProgress.current = false
      if (!silent) setSaving(false)
    }
  }

  if (loading) return <div className="qcr-loading">Loading…</div>

  const cover = COVER_GRADIENTS[quiz.coverIdx]?.value ?? COVER_GRADIENTS[0].value

  return (
    <div className="qcr-page">
      {/* Top bar */}
      <div className="qcr-topbar">
        <button type="button" onClick={handleBack} className="qcr-back">← Quizzes</button>
        <input
          className="qcr-title-input"
          placeholder="Quiz title…"
          value={quiz.title}
          onChange={e => updateQuiz({ title: e.target.value })}
          maxLength={120}
        />
        <div className="qcr-topbar__actions">
          {error && <span className="qcr-topbar__error">{error}</span>}
          <button className="btn btn-outline qcr-action-btn" onClick={() => save(false)} disabled={saving}>
            {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save draft'}
          </button>
          <button className="btn btn-primary qcr-action-btn" onClick={() => save(true)} disabled={saving}>
            Publish
          </button>
        </div>
      </div>

      <div className="qcr-body">
        {/* Sidebar */}
        <aside className="qcr-sidebar">
          {/* Type */}
          <div className="qcr-sidebar-section">
            <div className="qcr-sidebar-label">Quiz type</div>
            <div className="qcr-type-toggle">
              <button
                type="button"
                className={`qcr-type-btn${quiz.type === 'knowledge' ? ' qcr-type-btn--active' : ''}`}
                onClick={() => updateQuiz({ type: 'knowledge' })}
              >
                <span className="qcr-type-btn__icon">⚡</span>
                <span>Knowledge</span>
              </button>
              <button
                type="button"
                className={`qcr-type-btn${quiz.type === 'personality' ? ' qcr-type-btn--active' : ''}`}
                onClick={() => updateQuiz({ type: 'personality' })}
              >
                <span className="qcr-type-btn__icon">🌀</span>
                <span>Personality</span>
              </button>
            </div>
          </div>

          {/* Cover */}
          <div className="qcr-sidebar-section">
            <div className="qcr-sidebar-label">Cover</div>

            {/* Preview */}
            <div
              className="qcr-cover-preview"
              style={{
                background: (coverPreview || existingCoverUrl) ? undefined : COVER_GRADIENTS[quiz.coverIdx]?.value,
                backgroundImage: (coverPreview || existingCoverUrl) ? `url(${coverPreview || existingCoverUrl})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {(coverPreview || existingCoverUrl) && (
                <button
                  type="button"
                  className="qcr-cover-remove"
                  title="Remove image"
                  onClick={() => { setCoverFile(null); setCoverPreview(null); setExistingCoverUrl(null) }}
                >
                  ×
                </button>
              )}
            </div>

            {/* Upload */}
            <input
              ref={coverInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (!file) return
                setCoverFile(file)
                setCoverPreview(URL.createObjectURL(file))
                setExistingCoverUrl(null)
                e.target.value = ''
              }}
            />
            <button
              type="button"
              className="qcr-cover-upload-btn"
              onClick={() => coverInputRef.current?.click()}
            >
              {(coverPreview || existingCoverUrl) ? 'Change image' : 'Upload image'}
            </button>

            {/* Gradient swatches — only shown when no image */}
            {!(coverPreview || existingCoverUrl) && (
              <div className="qcr-cover-picker">
                {COVER_GRADIENTS.map((g, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`qcr-cover-swatch${quiz.coverIdx === i ? ' qcr-cover-swatch--active' : ''}`}
                    style={{ background: g.value }}
                    onClick={() => updateQuiz({ coverIdx: i })}
                    title={g.label}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Description */}
          <div className="qcr-sidebar-section">
            <div className="qcr-sidebar-label">Description</div>
            <textarea
              className="qcr-input qcr-description"
              placeholder="What's this quiz about?"
              value={quiz.description}
              onChange={e => updateQuiz({ description: e.target.value })}
              rows={3}
              maxLength={500}
            />
          </div>

          {/* Fandom */}
          <div className="qcr-sidebar-section">
            <div className="qcr-sidebar-label">Fandom</div>
            <select
              className="qcr-input"
              value={quiz.fandom}
              onChange={e => updateQuiz({ fandom: e.target.value })}
            >
              {COMMON_FANDOMS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>

          {/* Tags */}
          <div className="qcr-sidebar-section">
            <div className="qcr-sidebar-label">Tags</div>
            <TagInput tags={quiz.tags} onChange={v => updateQuiz({ tags: v })} />
          </div>

          {/* Comments toggle */}
          <div className="qcr-sidebar-section">
            <label className="qcr-sidebar-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={quiz.disableComments}
                onChange={(e) => updateQuiz({ disableComments: e.target.checked })}
              />
              Disable comments
            </label>
          </div>

          {/* Randomize answer order toggle */}
          <div className="qcr-sidebar-section">
            <label className="qcr-sidebar-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={quiz.randomizeAnswers}
                onChange={(e) => updateQuiz({ randomizeAnswers: e.target.checked })}
              />
              Shuffle answer order
            </label>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)', marginTop: 4, marginLeft: 24 }}>
              Each player sees answer choices in a random order.
            </div>
          </div>

          {/* Outcomes (personality only) */}
          {quiz.type === 'personality' && (
            <div className="qcr-sidebar-section qcr-outcomes-section">
              <div className="qcr-sidebar-label">Outcomes</div>
              <div className="qcr-outcomes-list">
                {quiz.outcomes.map((o, i) => (
                  <div key={o.tempId ?? i} className="qcr-outcome-row">
                    <input
                      className="qcr-input qcr-input--sm"
                      placeholder={`Result ${i + 1} title…`}
                      value={o.title}
                      onChange={e => {
                        const next = quiz.outcomes.map((x, j) => j === i ? { ...x, title: e.target.value } : x)
                        updateQuiz({ outcomes: next })
                      }}
                      maxLength={80}
                    />
                    <textarea
                      className="qcr-input qcr-outcome-desc"
                      placeholder="Description…"
                      value={o.description}
                      onChange={e => {
                        const next = quiz.outcomes.map((x, j) => j === i ? { ...x, description: e.target.value } : x)
                        updateQuiz({ outcomes: next })
                      }}
                      rows={2}
                      maxLength={500}
                    />
                    {quiz.outcomes.length > 1 && (
                      <button
                        type="button"
                        className="qcr-remove-outcome"
                        onClick={() => updateQuiz({ outcomes: quiz.outcomes.filter((_, j) => j !== i) })}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="qcr-add-outcome"
                  onClick={() => updateQuiz({ outcomes: [...quiz.outcomes, makeOutcome()] })}
                >
                  + Add outcome
                </button>
              </div>
            </div>
          )}

          {/* Question tabs */}
          <div className="qcr-sidebar-section qcr-q-tabs-section">
            <div className="qcr-sidebar-label">Questions ({quiz.questions.length})</div>
            <div className="qcr-q-tabs">
              {quiz.questions.map((q, i) => (
                <button
                  key={q.id}
                  type="button"
                  className={`qcr-q-tab${activeQIdx === i ? ' qcr-q-tab--active' : ''}`}
                  onClick={() => setActiveQ(i)}
                >
                  <span className="qcr-q-tab__num">Q{i + 1}</span>
                  <span className="qcr-q-tab__text">{q.text || 'New question'}</span>
                </button>
              ))}
            </div>
            <button type="button" className="qcr-add-q" onClick={addQuestion}>+ Add question</button>
          </div>
        </aside>

        {/* Main editor */}
        <main className="qcr-main">
          {quiz.questions[activeQIdx] && (
            <QuestionEditor
              question={quiz.questions[activeQIdx]}
              quizType={quiz.type}
              outcomes={quiz.outcomes}
              onChange={q => updateQuestion(activeQIdx, q)}
              onDelete={() => deleteQuestion(activeQIdx)}
              questionNum={activeQIdx + 1}
              totalQuestions={quiz.questions.length}
            />
          )}

          {/* Navigation */}
          <div className="qcr-q-nav">
            {activeQIdx > 0 && (
              <button type="button" className="btn btn-outline qcr-nav-btn" onClick={() => setActiveQ(i => i - 1)}>
                ← Previous
              </button>
            )}
            {activeQIdx < quiz.questions.length - 1 ? (
              <button type="button" className="btn btn-outline qcr-nav-btn" onClick={() => setActiveQ(i => i + 1)}>
                Next →
              </button>
            ) : (
              <button type="button" className="btn btn-outline qcr-nav-btn" onClick={addQuestion}>
                + New question
              </button>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}
